import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@/lib/i18n";

/**
 * Phase 8.A QA-3 / Phase 8.B QA-8 — Premium-feel "AI is thinking" rotator
 * shown while the Portfolio AI Review job is running.
 *
 * Phase 8.B QA-8 — fully locale-aware. The rotator now resolves each
 * message through the active i18n namespace so the German UI never sees
 * an English loader (previously the default array was hardcoded English
 * and the rotator was always mounted with no `messages` prop).
 *
 * Tone is investor-playful but neutral. The "Buffett lens" phrasing
 * refers to a well-known value-investing analytical style, NOT an
 * endorsement by Warren Buffett or any actual involvement by him.
 * Falls back to a single static message when the user has
 * ``prefers-reduced-motion``.
 */
const ROTATING_LOADER_KEYS = [
  "portfolio.loader.running_value_lens",
  "portfolio.loader.buffett_lens",
  "portfolio.loader.scanning_value_traps",
  "portfolio.loader.checking_carriers",
  "portfolio.loader.stress_testing",
  "portfolio.loader.price_vs_value",
  "portfolio.loader.separating_signal",
];

export default function LoadingMessageRotator({ messages, intervalMs = 2200 }) {
  const { t, locale } = useTranslation();
  const [idx, setIdx] = useState(0);
  // Resolve the rotating list from the active locale every render so
  // switching UI language between EN ↔ DE updates the loader without a
  // remount. If a parent explicitly passes a `messages` prop, honour it.
  // NOTE: `locale` is deliberately included in the dependency array
  // even though it isn't referenced directly — it's a proxy for the
  // active i18n bundle that `t(...)` reads from.  Rebuilding the list
  // when the locale changes is exactly the behaviour that keeps the
  // loader bilingual.  eslint-disable acknowledges the intentional dep.
  const list = useMemo(() => {
    if (Array.isArray(messages) && messages.length > 0) return messages;
    const resolved = ROTATING_LOADER_KEYS.map((k) => t(k)).filter(Boolean);
    return resolved.length > 0 ? resolved : ROTATING_LOADER_KEYS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, t, locale]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced) return undefined;
    const id = window.setInterval(() => setIdx((i) => (i + 1) % list.length), intervalMs);
    return () => window.clearInterval(id);
  }, [list, intervalMs]);

  return (
    <span
      key={idx}
      data-testid="portfolio-loading-message"
      className="transition-opacity duration-300"
      style={{ opacity: 1 }}
    >
      {list[idx % list.length]}
    </span>
  );
}
